import type { APIOpportunity } from "@/lib/whale-watch/whale-watch-shared";

export type LeagueId = string;
export type FamilyId = string;
export type EventId = string;
export type BookId = string;
export type LiveStream = "optimizer" | "fantasy-props";
export type EntityKey = string;
export type RowId = string;
export type Odds = number;

export type CatalogTopic = { readonly kind: "catalog" };
export type DfsTopic = { readonly kind: "dfs" };
export type BoardTopic = { readonly kind: "board"; readonly league: LeagueId; readonly family: FamilyId };
export type LadderTopic = {
  readonly kind: "ladder";
  readonly league: LeagueId;
  readonly event: EventId;
  readonly family: FamilyId;
  readonly entity: EntityKey;
};
export type PropsTopic = { readonly kind: "props"; readonly league: LeagueId; readonly book: BookId };
export type OptimizerTopic = { readonly kind: "optimizer"; readonly league: LeagueId; readonly book: BookId };
export type BookRowsTopic = PropsTopic | OptimizerTopic;
export type WhaleTier = "default" | "wide";
export type WhaleTopic = { readonly kind: "whale"; readonly sport: string; readonly tier: WhaleTier };
export type WhaleRowTopic = { readonly kind: "whale_row"; readonly id: RowId };
export type DiscrepanciesTopic = { readonly kind: "discrepancies"; readonly league: LeagueId };
export type ArbitrageTopic = { readonly kind: "arbitrage"; readonly league: LeagueId };
export type LowHoldTier = "default" | "wide";
export type LowHoldTopic = { readonly kind: "lowhold"; readonly league: LeagueId; readonly tier: LowHoldTier };
export type SniperTopic = { readonly kind: "sniper"; readonly league: LeagueId };
export type SniperLiveTopic = { readonly kind: "sniper_live"; readonly league: LeagueId };
export type SniperRowsTopic = SniperTopic | SniperLiveTopic;
export type RaceTopic = { readonly kind: "race"; readonly book: BookId };
export type InplayTopic = { readonly kind: "live"; readonly league: LeagueId };
export type InplayEventTopic = { readonly kind: "live_event"; readonly league: LeagueId; readonly event: EventId; readonly family: FamilyId };
export type CellKey = string;
export type WhalesTopic = { readonly kind: "whales"; readonly league: LeagueId; readonly event: EventId; readonly key: CellKey };
export type Topic = CatalogTopic | DfsTopic | BoardTopic | LadderTopic | PropsTopic | OptimizerTopic | WhaleTopic | WhaleRowTopic | DiscrepanciesTopic | ArbitrageTopic | LowHoldTopic | SniperTopic | SniperLiveTopic | RaceTopic | InplayTopic | InplayEventTopic | WhalesTopic;

export const LIVE_PERIOD_UNITS = ["quarter", "half", "period", "inning", "set", "round", "overtime", "extra_time", "shootout"] as const;
export type LivePeriod = {
  readonly unit: (typeof LIVE_PERIOD_UNITS)[number];
  readonly number: number;
  readonly half: "top" | "bottom" | null;
  readonly break: boolean;
};
export type LiveClock = { readonly seconds: number; readonly running: boolean; readonly atMs: number };
export type LiveSide = "home" | "away";
export type LiveSetGames = { readonly home: number; readonly away: number };
export type LiveSets = { readonly games: readonly LiveSetGames[]; readonly homePoints: string; readonly awayPoints: string; readonly serving: LiveSide | null };
export type LivePlay = { readonly text: string; readonly atMs: number };
export const LIVE_PAUSE_KINDS = ["period_break", "timeout", "tv_timeout", "medical_timeout", "review", "delay", "game_break", "other"] as const;
export type LivePauseKind = (typeof LIVE_PAUSE_KINDS)[number];
export type LivePause = { readonly kind: LivePauseKind };
export const PERIOD_BREAK: LivePause = { kind: "period_break" };
export type InplayGame = {
  readonly id: EventId;
  readonly name: string;
  readonly home: string;
  readonly away: string;
  readonly homeShort: string | null;
  readonly awayShort: string | null;
  readonly homeLogoUrl: string | null;
  readonly awayLogoUrl: string | null;
  readonly startMs: number;
  readonly families: readonly FamilyId[];
  readonly gameFamilies: readonly FamilyId[];
  readonly status: InplayStatus | null;
};
export type InplayList = { readonly games: ReadonlyMap<EventId, InplayGame>; readonly ended: ReadonlySet<EventId> };
export type InplayCell = {
  readonly id: string;
  readonly book: BookId;
  readonly side: string;
  readonly market: string;
  readonly entity: Entity | null;
  readonly line: number | null;
  readonly alt: boolean;
  readonly odds: number;
  readonly suspended: boolean;
  readonly stale: boolean;
  readonly link: string | null;
  readonly appLink: string | null;
};
export type InplayEvent = { readonly cells: ReadonlyMap<string, InplayCell> };
export type LiveScore = { readonly home: string; readonly away: string };
export type InplayStatus = {
  readonly period: LivePeriod | null;
  readonly clock: LiveClock | null;
  readonly score: LiveScore | null;
  readonly sets: LiveSets | null;
  readonly periods: readonly LiveSetGames[] | null;
  readonly pause: LivePause | null;
  readonly plays: readonly LivePlay[];
  readonly paused: boolean;
};
export const IN_PLAY: InplayStatus = { period: null, clock: null, score: null, sets: null, periods: null, pause: null, plays: [], paused: false };

export type RaceLegBook = { readonly book: BookId; readonly fair: number };
export type RaceLeg = {
  readonly row: RowId | null;
  readonly league: LeagueId;
  readonly startMs: number;
  readonly player: string;
  readonly pos: string;
  readonly team: string;
  readonly opp: string;
  readonly img: string | null;
  readonly market: string;
  readonly line: number;
  readonly side: string;
  readonly fair: number | null;
  readonly odds: number | null;
  readonly ev: number | null;
  readonly assumed: boolean;
  readonly books: readonly RaceLegBook[] | null;
};
export type Race = {
  readonly id: string;
  readonly book: BookId;
  readonly sinceMs: number;
  readonly mult: number;
  readonly boost: number;
  readonly seats: number;
  readonly taken: number;
  readonly min: number;
  readonly max: number;
  readonly hit: number | null;
  readonly need: number;
  readonly ev: number | null;
  readonly assumed: number;
  readonly legs: readonly RaceLeg[];
};
export type RaceList = { readonly races: ReadonlyMap<string, Race> };

export type TopicValue<T extends Topic> =
  T extends CatalogTopic ? Catalog
    : T extends DfsTopic ? Dfs
    : T extends BoardTopic ? Board
      : T extends LadderTopic ? Ladder
        : T extends BookRowsTopic ? Props
        : T extends DiscrepanciesTopic ? Discrepancies
        : T extends ArbitrageTopic ? Arbitrage
        : T extends LowHoldTopic ? LowHold
        : T extends SniperRowsTopic ? Sniper
        : T extends RaceTopic ? RaceList
        : T extends InplayTopic ? InplayList
        : T extends InplayEventTopic ? InplayEvent
        : T extends WhalesTopic ? WhalesLadder
          : T extends WhaleTopic ? WhaleList
            : WhaleDetail;

export type OddsDisplay = "american" | "decimal" | "percent";
export type LineKind = "none" | "spread" | "total";
export type StakeKind = "depth" | "limit";
export type LeagueStatus = "live" | "warming" | "reconnecting";

export type FamilyKind = "lines" | "props";
export const FAMILY_LAYOUTS = ["sides", "named", "results"] as const;
export type FamilyLayout = (typeof FAMILY_LAYOUTS)[number];

export type MarketSpec = {
  readonly id: string;
  readonly label: string;
  readonly name: string;
  readonly period: string;
  readonly line: LineKind;
  readonly outcomes: readonly string[];
  readonly labels?: readonly string[];
  readonly keys: readonly string[];
};

export type FamilySummary = {
  readonly id: FamilyId;
  readonly label: string;
  readonly kind: FamilyKind;
  readonly layout: FamilyLayout;
  readonly group?: string;
  readonly category: string;
  readonly events: number;
  readonly rows: number;
  readonly markets: readonly MarketSpec[];
};

export type FamilyShape = Pick<FamilySummary, "kind" | "layout">;

export type SkippedFamily = { readonly id: FamilyId; readonly layout: string };

export type LeagueSummary = {
  readonly id: LeagueId;
  readonly status: LeagueStatus;
  readonly staleBooks: ReadonlySet<BookId>;
  readonly events: number;
  readonly families: readonly FamilySummary[];
  readonly skippedFamilies: readonly SkippedFamily[];
};

export type Catalog = {
  readonly books: ReadonlyMap<BookId, { readonly stake: StakeKind | null; readonly standard: number | null; readonly resells: BookId | null }>;
  readonly leagues: ReadonlyMap<LeagueId, LeagueSummary>;
  readonly leagueOrder: readonly LeagueId[];
};

export type DfsLeague = {
  readonly floor: number;
  readonly books: ReadonlyMap<BookId, readonly number[]>;
};

export type Dfs = ReadonlyMap<LeagueId, DfsLeague>;

export type EventHeader = {
  readonly id: EventId;
  readonly name: string;
  readonly startMs: number;
  readonly home: string;
  readonly away: string;
  readonly homeShort: string | null;
  readonly awayShort: string | null;
  readonly homeLogoUrl: string | null;
  readonly awayLogoUrl: string | null;
  readonly homeRank?: number;
  readonly awayRank?: number;
  readonly homeConf?: string;
  readonly awayConf?: string;
  readonly props: number;
  readonly live?: InplayStatus;
  readonly liveProps?: boolean;
};

export type Entity = { readonly key: EntityKey; readonly name: string; readonly order?: number };

export type PlayerIdentity = { readonly id: string; readonly imageUrl: string | null; readonly position: string | null };

export type BookCell =
  | {
      readonly at: "main";
      readonly book: BookId;
      readonly odds: readonly (Odds | null)[];
      readonly stakes: readonly (number | null)[] | null;
      readonly held?: readonly boolean[];
    }
  | {
      readonly at: "off";
      readonly book: BookId;
      readonly line: number;
      readonly odds: readonly (Odds | null)[];
      readonly stakes: readonly (number | null)[] | null;
      readonly held?: readonly boolean[];
    };

export type MarketQuote = {
  readonly main: number | null;
  readonly lines: number;
  readonly books: readonly BookCell[];
  readonly consensus?: readonly [number, number];
};

export type BoardRow = {
  readonly id: RowId;
  readonly eventId: EventId;
  readonly entity: Entity | null;
  readonly player: PlayerIdentity | null;
  readonly markets: readonly (MarketQuote | null)[];
};

export type Board = {
  readonly events: ReadonlyMap<EventId, EventHeader>;
  readonly rows: ReadonlyMap<RowId, BoardRow>;
};

export type LadderOutcome = {
  readonly odds: Odds;
  readonly stake: number | null;
  readonly depth: readonly (readonly [Odds, number])[];
  readonly link: string | null;
  readonly appLink: string | null;
  readonly selectionId: string | null;
  readonly side: string | null;
  readonly held?: boolean;
};

export type LadderBook = { readonly book: BookId; readonly outcomes: readonly (LadderOutcome | null)[] };

export type LadderLine = { readonly slot: number; readonly line: number; readonly books: readonly LadderBook[] };

export type LadderLineId = `${number}:${number}`;

export type Ladder = {
  readonly mains: readonly (number | null)[];
  readonly lines: ReadonlyMap<LadderLineId, LadderLine>;
};

export type BetLinks = readonly [generic: string, mobile: string, desktop: string];

export type PropsBook = {
  readonly book: BookId;
  readonly line: number | null;
  readonly odds: Odds;
  readonly oppOdds: Odds | null;
  readonly fair: number | null;
  readonly limit: number | null;
  readonly oppLimit: number | null;
  readonly selectionId: string;
  readonly links: BetLinks;
  readonly eligible: boolean;
};

export type WhalesSide = { readonly name: string; readonly odds: Odds | null; readonly money: number; readonly count: number; readonly fair: number | null };
export type WhalesSummary = readonly [WhalesSide, WhalesSide];
export type WhalesCell = { readonly summary: WhalesSummary; readonly without: ReadonlyMap<BookId, WhalesSummary> };
export type WhaleOrder = { readonly book: BookId; readonly odds: Odds; readonly stake: number };
export type WhalesLadderSide = { readonly name: string; readonly orders: readonly WhaleOrder[]; readonly total: number };
export type WhalesLadder = { readonly sides: readonly WhalesLadderSide[] };

export type FairSource = "market" | "model" | "blend";

export const PROMO_KINDS = ["gimme", "slice", "power", "taco", "hotdog", "flash", "free"] as const;
export type PromoKind = (typeof PROMO_KINDS)[number];

export type PropsPromo = {
  readonly kind: PromoKind;
  readonly name: string;
  readonly cap: number | null;
  readonly minPicks: number;
  readonly maxPerLineup: number;
  readonly originalLine: number | null;
  readonly originalMultiplier: number | null;
};

export type PropsRow = {
  readonly id: RowId;
  readonly eventId: EventId;
  readonly league: LeagueId;
  readonly marketType: "player" | "game";
  readonly marketKey: string;
  readonly market: string;
  readonly entity: Entity;
  readonly player: PlayerIdentity | null;
  readonly team: string;
  readonly side: string;
  readonly line: number | null;
  readonly odds: Odds;
  readonly oppOdds: Odds | null;
  readonly limit: number | null;
  readonly oppLimit: number | null;
  readonly fair: number;
  readonly ev: number;
  readonly peers: number;
  readonly fairSource: FairSource;
  readonly selectionId: string;
  readonly oppSelectionId: string;
  readonly links: BetLinks;
  readonly books: readonly PropsBook[];
  readonly promo: PropsPromo | null;
  readonly pick: string | null;
  readonly cell: CellKey | null;
  readonly whales: WhalesSummary | null;
};

export type Props = {
  readonly events: ReadonlyMap<EventId, EventHeader>;
  readonly rows: ReadonlyMap<RowId, PropsRow>;
  readonly whales: ReadonlyMap<CellKey, WhalesCell>;
};

export type DiscrepancyBook = {
  readonly book: BookId;
  readonly line: number;
  readonly overOdds: Odds | null;
  readonly underOdds: Odds | null;
  readonly multiplier: number | null;
  readonly overSelectionId: string;
  readonly underSelectionId: string;
  readonly overLinks: BetLinks;
  readonly underLinks: BetLinks;
  readonly overFair: number | null;
  readonly underFair: number | null;
};

export type DiscrepancySide = "over" | "under";

export type DiscrepancyBest = { readonly book: BookId; readonly line: number; readonly side: DiscrepancySide; readonly fair: number | null };

export type DiscrepancyRow = {
  readonly id: RowId;
  readonly eventId: EventId;
  readonly league: LeagueId;
  readonly marketKey: string;
  readonly market: string;
  readonly entity: Entity;
  readonly player: PlayerIdentity | null;
  readonly team: string;
  readonly modal: number;
  readonly min: number;
  readonly max: number;
  readonly spread: number;
  readonly books: readonly DiscrepancyBook[];
  readonly best: DiscrepancyBest;
  readonly fairModal: { readonly over: number; readonly under: number } | null;
  readonly peers: number;
};

export type Discrepancies = {
  readonly events: ReadonlyMap<EventId, EventHeader>;
  readonly rows: ReadonlyMap<RowId, DiscrepancyRow>;
};

export type SniperRow = PropsRow & { readonly book: BookId };

export type Sniper = {
  readonly events: ReadonlyMap<EventId, EventHeader>;
  readonly rows: ReadonlyMap<RowId, SniperRow>;
  readonly whales: ReadonlyMap<CellKey, WhalesCell>;
};

export type ArbKind = "two_way" | "threshold" | "three_way";

export type ArbLeg = {
  readonly book: BookId;
  readonly side: string;
  readonly market: string;
  readonly line: number;
  readonly odds: number;
  readonly limit: number | null;
  readonly selection: string;
  readonly links: BetLinks;
  readonly fair: number | null;
  readonly buyNo: boolean;
  readonly feeIn: boolean;
};

export type ArbPick = {
  readonly legs: readonly number[];
  readonly stakes: readonly number[];
  readonly roi: number;
  readonly maxStake: number | null;
  readonly middle: boolean;
  readonly check: boolean;
};

export type ArbRow = {
  readonly id: RowId;
  readonly eventId: EventId;
  readonly league: LeagueId;
  readonly kind: ArbKind;
  readonly marketKey: string;
  readonly marketType: "game" | "player";
  readonly market: string;
  readonly entity: Entity;
  readonly player: PlayerIdentity | null;
  readonly team: string;
  readonly legs: readonly ArbLeg[];
  readonly picks: readonly ArbPick[];
};

export type LowHoldRow = {
  readonly id: RowId;
  readonly eventId: EventId;
  readonly league: LeagueId;
  readonly kind: ArbKind;
  readonly marketKey: string;
  readonly marketType: "game" | "player";
  readonly market: string;
  readonly entity: Entity;
  readonly player: PlayerIdentity | null;
  readonly team: string;
  readonly hold: number;
  readonly books: number;
  readonly legs: readonly ArbLeg[];
};

export type Arbitrage = {
  readonly events: ReadonlyMap<EventId, EventHeader>;
  readonly rows: ReadonlyMap<RowId, ArbRow>;
};

export type LowHold = {
  readonly events: ReadonlyMap<EventId, EventHeader>;
  readonly rows: ReadonlyMap<RowId, LowHoldRow>;
};

export type WhaleState = "fresh" | "stale" | "unavailable";
export type WhaleSideName = "OVER" | "UNDER" | "HOME" | "AWAY" | "DRAW" | "YES" | "NO";

export type WhalePrice = {
  readonly book: BookId;
  readonly odds_american: number;
  readonly size: number | null;
  readonly bet_link: string;
  readonly bet_link_mobile?: string;
  readonly edge_percent?: number;
  readonly edge_supporting_liquidity: number;
  readonly fee_adjusted?: boolean;
};

export type WhaleRow = {
  readonly market_data: {
    readonly id: RowId;
    readonly event_id: EventId;
    readonly event: string;
    readonly event_start: string;
    readonly home_team: string;
    readonly away_team: string;
    readonly league: string;
    readonly sport: string;
    readonly entity_type: string;
    readonly entity_name: string;
    readonly market: string;
    readonly market_type: string;
    readonly line: number;
    readonly market_arity?: number;
    readonly updated_at: string;
    readonly image_url?: string;
    readonly home_logo_url?: string;
    readonly away_logo_url?: string;
  };
  readonly best_book: BookId;
  readonly best_odds: number;
  readonly fair_outcome_share?: number;
  readonly fair_odds?: number;
  readonly edge_percent?: number;
  readonly edge_supporting_liquidity: number;
  readonly same_side_conviction_liquidity: number;
  readonly recommended_side: {
    readonly side: WhaleSideName;
    readonly line: number;
    readonly demand: number;
    readonly money_at_risk?: number;
    readonly prices: readonly WhalePrice[];
  };
  readonly opposite_side: {
    readonly side: WhaleSideName;
    readonly line: number;
    readonly demand: number;
    readonly money_at_risk?: number;
    readonly peer_books: readonly BookId[];
  };
};

export type WhaleList = {
  readonly state: WhaleState;
  readonly missing: readonly string[];
  readonly cutLowestEdge: number;
  readonly rows: ReadonlyMap<RowId, WhaleRow>;
};

export type WhaleDetail = { readonly state: WhaleState; readonly row: APIOpportunity };

export type TopicPhase = "loading" | "live" | "resyncing" | "ended";
export const END_REASONS = ["unknown_topic", "topic_limit", "forbidden", "event_started", "event_removed", "row_removed", "live_ev_unavailable"] as const;
export type EndReason = (typeof END_REASONS)[number];

export type TopicState<V> = {
  readonly phase: TopicPhase;
  readonly seq: number;
  readonly value: V | null;
  readonly ended: EndReason | null;
  readonly sourceAt: number | null;
};

export type RowRef = { readonly eventId: EventId; readonly entity: Entity | null };

export type BoardView = {
  readonly phase: TopicPhase;
  readonly ended: EndReason | null;
  readonly events: ReadonlyMap<EventId, EventHeader>;
  readonly rows: ReadonlyMap<RowId, RowRef>;
  readonly rowIds: readonly RowId[];
};

export type EventRowsView<R> = {
  readonly phase: TopicPhase;
  readonly pending: number;
  readonly ended: EndReason | null;
  readonly events: ReadonlyMap<EventId, EventHeader>;
  readonly rows: ReadonlyMap<RowId, R>;
  readonly rowIds: readonly RowId[];
};
export type PropsView = EventRowsView<PropsRow>;
export type DiscrepanciesView = EventRowsView<DiscrepancyRow>;
export type ArbitrageView = EventRowsView<ArbRow>;
export type LowHoldView = EventRowsView<LowHoldRow>;
export type SniperView = EventRowsView<SniperRow>;

export type ConnectionStatus =
  | "idle"
  | "connecting"
  | "live"
  | "reconnecting"
  | "paused"
  | "locked"
  | "limited"
  | "rate_limited";

export type TopicKey =
  | readonly ["catalog"]
  | readonly ["dfs"]
  | readonly ["board", LeagueId, FamilyId]
  | readonly ["ladder", LeagueId, EventId, FamilyId, EntityKey]
  | readonly ["props", LeagueId, BookId]
  | readonly ["optimizer", LeagueId, BookId]
  | readonly ["whale", string, WhaleTier]
  | readonly ["discrepancies", LeagueId]
  | readonly ["arbitrage", LeagueId]
  | readonly ["lowhold", LeagueId, LowHoldTier]
  | readonly ["sniper", LeagueId]
  | readonly ["sniper_live", LeagueId]
  | readonly ["race", BookId]
  | readonly ["live", LeagueId]
  | readonly ["live_event", LeagueId, EventId, FamilyId]
  | readonly ["whales", LeagueId, EventId, CellKey]
  | readonly ["whale_row", RowId];

export function topicKey(topic: Topic): TopicKey {
  switch (topic.kind) {
    case "catalog": return ["catalog"];
    case "dfs": return ["dfs"];
    case "board": return ["board", topic.league, topic.family];
    case "ladder": return ["ladder", topic.league, topic.event, topic.family, topic.entity];
    case "props": return ["props", topic.league, topic.book];
    case "optimizer": return ["optimizer", topic.league, topic.book];
    case "discrepancies": return ["discrepancies", topic.league];
    case "arbitrage": return ["arbitrage", topic.league];
    case "lowhold": return ["lowhold", topic.league, topic.tier];
    case "sniper": return ["sniper", topic.league];
    case "sniper_live": return ["sniper_live", topic.league];
    case "race": return ["race", topic.book];
    case "live": return ["live", topic.league];
    case "live_event": return ["live_event", topic.league, topic.event, topic.family];
    case "whales": return ["whales", topic.league, topic.event, topic.key];
    case "whale": return ["whale", topic.sport, topic.tier];
    case "whale_row": return ["whale_row", topic.id];
  }
}

export function topicId(topic: Topic): string {
  return JSON.stringify(topicKey(topic));
}

export function ladderLineId(slot: number, line: number): LadderLineId {
  return `${slot}:${line}`;
}
